import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, take, tap } from 'rxjs';

import { GridComponent } from '@grid/grid.component';
import {
  GridApi,
  GridColumn,
  GridOptions,
  GridSync,
  IIdentity,
} from '@grid/models';
import { StaticMessage } from '@shared/enums';
import { setAllObjectKeys } from '@shared/utils';

@UntilDestroy()
@Component({
  template: '',
})
export abstract class GridBaseComponent<T extends IIdentity> {
  //#region Inputs

  @Input({ required: true }) gridData: T[];
  @Input() canEdit: boolean;
  @Input() selectedSubprojectId: number;

  //#endregion

  //#region Outputs

  @Output() gridRegistered = new EventEmitter<GridApi<T>>();
  @Output() navigatedToBaseRoute = new EventEmitter<boolean>();

  //#endregion

  //#region Templates

  @ViewChild(GridComponent)
  protected gridComponent: GridComponent<T>;

  //#endregion

  gridOptions$ = new BehaviorSubject<GridOptions<T>>(null);

  gridColumns$ = new BehaviorSubject<GridColumn<T>[]>(null);

  gridApi$ = new BehaviorSubject<GridApi<T>>(null);

  StaticMessage = StaticMessage;

  protected activatedRoute?: ActivatedRoute;
  protected router?: Router;

  get api(): GridApi<T> {
    return this.gridComponent?.api;
  }

  hasChanges(): boolean {
    return this.gridComponent?.api?.hasChanges();
  }

  getChanges(): GridSync<T> {
    return this.gridComponent?.api?.getChanges();
  }

  getChangesWithUpdatedPriorities(): GridSync<T> {
    return this.api?.getChangesWithUpdatedPriorities();
  }

  reset(): void {
    this.gridComponent?.api?.reset();
  }

  protected openModalFromRoute(
    gridApi: GridApi<T>,
    additionalData?: any
  ): void {
    setTimeout(() => {
      this.activatedRoute?.queryParams
        .pipe(
          untilDestroyed(this),
          take(1),
          tap((params) => {
            if (!params?.modal || !params.entity) {
              this.navigateToBaseRoute();
              return;
            }

            if (
              !this.gridColumns$?.value?.find(
                (x) => x.columnDef === params.modal
              )
            ) {
              this.navigateToBaseRoute();
              return;
            }

            const entity = this.gridData.find(
              (x) => x.id.toString() === params.entity
            );

            if (!entity) {
              this.navigateToBaseRoute();
              return;
            }

            gridApi.openEdit(params.modal, entity.id, additionalData);
          })
        )
        .subscribe();
    });
  }

  protected navigateToBaseRoute(exceptionList?: string[]): void {
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };

    if (!Object.keys(queryParams).length) {
      return;
    }

    setAllObjectKeys(queryParams, null, exceptionList);

    this.router?.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.navigatedToBaseRoute.emit(true);
  }

  protected navigateToBaseRouteWithParams(params: any): void {
    this.router?.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...params },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected checkForImportDialogParams(): boolean {
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    return queryParams.modal === 'import';
  }

  //#region Abstract methods

  protected abstract getGridOptions(): GridOptions<T>;

  protected abstract getGridColumns(): GridColumn<T>[];

  //#endregion
}
